import React from "react";
import _ from "lodash";
import { capitalize } from "../../../utils/textFunctions";
import { formattedDateWithDefault } from "../../../utils/formattedDate";
import moment from "moment";
import {
  ExtensionURLs,
  ActionLabels,
  getValueTypeByAttribute,
  GenericOverrideURLs,
  getRestoreExtensionURLByAttribute,
} from "./GoldenRecordExclusion/Utils";
import { createRestoreAuditExtensions } from "./MultipleAttributeOverride/PrepareOverrides";
import {
  isEmptyName,
  isEmptyTelecom,
  isEmptyAddress,
  isEmptyIdentifier,
} from "../../../utils/functionUtils";

/**
 * Determines if a given attribute in an object is marked as "new" via an override.
 *
 * @param {object} object - The object containing the attribute data.
 * @returns {boolean} - True if the attribute has a override extension valueString as "new", false otherwise.
 */
export const isNewAttribute = (object) => {
  return (
    object?.extension?.find((ext) => ext.url === ExtensionURLs.genericOverride)
      ?.valueString === "new"
  );
};

/**
 * Determines if a given attribute in an object is empty.
 *
 * @param {string} attribute - The attribute type (e.g., "name", "address").
 * @param {object} object - The object containing the attribute data.
 * @returns {boolean} - True if the attribute is empty, false otherwise.
 */
export const isEmptyAttribute = (attribute, object) => {
  switch (attribute) {
    case "name":
      return isEmptyName(object);
    case "address":
      return isEmptyAddress(object);
    case "identifier":
      return isEmptyIdentifier(object);
    case "email":
    case "phone":
      return isEmptyTelecom(object);
    default:
      return false;
  }
};

/**
 * Determines the override type for a given attribute and object.
 *
 * @param {string} attribute - The attribute type (e.g., "name", "address").
 * @param {object} object - The object containing the attribute data.
 * @returns {string} - The override type: "New", "Invalid", "Inactive", or "Replace".
 */
export const getOverrideType = (attribute, object) => {
  switch (true) {
    case isNewAttribute(object):
      return "New";
    case isEmptyAttribute(attribute, object):
      return "Invalid";
    case object?.use?.toUpperCase() === "OLD":
      return "Inactive";
    default:
      return "Replace";
  }
};

export const overrideSummaryForName = (name, type) => {
  const extensionArray = name.hasOwnProperty("extension")
    ? name.extension.filter(
        (item) =>
          item.url ===
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
      )
    : [];

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(name);

  let extensionMap = {
    Field: "Name",
    overrideIconClicked: true,
    originalFieldName: "name",
    reviewOverrideType: type,
  };

  if (extensionArray.length > 0) {
    const elementName = extensionArray[0].valueHumanName;
    const extAttrCode = elementName.extension
      ? elementName.extension.filter((e) => e.url.includes("attrCode"))
      : "";
    extensionMap = {
      ...extensionMap,
      "Family Name": elementName.family ? elementName.family : "-",
      "Given Name": elementName.given
        ? elementName.given.map((givenName, gnKey) => {
            return <div key={gnKey}>{givenName}</div>;
          })
        : "-",
      "Suffix Name": elementName.suffix
        ? elementName.suffix.map((suffixName, sKey) => {
            return <div key={sKey}>{suffixName}</div>;
          })
        : "-",

      Use: elementName.use ? capitalize(elementName.use) : "-",
      Attribute: extAttrCode[0] ? extAttrCode[0].valueString : "-",
      "Source Timestamp":
        elementName.period && elementName.period.start
          ? formattedDateWithDefault(elementName.period.start)
          : "-",
      "Mark as": getOverrideType(extensionMap.originalFieldName, name),
      originalFieldData: elementName,
      "Overridden By": overriddenByValue ? overriddenByValue : "-",
      "Override Reason": overrideReasonValue ? overrideReasonValue : "-",
      "Override Timestamp": overrideTimestampValue
        ? formattedDateWithDefault(overrideTimestampValue)
        : "-",
    };
  }
  return extensionMap;
};

export const overrideSummaryForAddress = (address, summaryType, type) => {
  const extensionArray = address.hasOwnProperty("extension")
    ? address.extension.filter(
        (item) =>
          item.url ===
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
      )
    : [];

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(address);

  let extensionMap = {
    Field: type === "birthplaceAddress" ? "Birth Place Address" : "Address",
    overrideIconClicked: true,
    originalFieldName: "address",
    reviewOverrideType: summaryType,
  };

  if (extensionArray.length > 0) {
    const elementAddr = extensionArray[0].valueAddress;
    const extAttrCode =
      elementAddr && elementAddr.extension
        ? elementAddr.extension.filter((e) => e.url.includes("attrCode"))
        : "";
    const srcTmStmpExt =
      elementAddr && elementAddr.extension
        ? elementAddr.extension.filter((e) =>
            e.url.includes("#address-srcTmStmp"),
          )
        : "";

    extensionMap = {
      ...extensionMap,
      "Address Line":
        elementAddr && elementAddr.line
          ? elementAddr.line.map((addressLine, lKey) => {
              return <div key={lKey}>{addressLine}</div>;
            })
          : "-",
      City: elementAddr && elementAddr.city ? elementAddr.city : "-",
      District:
        elementAddr && elementAddr.district ? elementAddr.district : "-",
      State: elementAddr && elementAddr.state ? elementAddr.state : "-",
      Country: elementAddr && elementAddr.country ? elementAddr.country : "-",
      "Postal Code":
        elementAddr && elementAddr.postalCode ? elementAddr.postalCode : "-",
      Use: elementAddr && elementAddr.use ? elementAddr.use : "-",
      Attribute: extAttrCode[0] ? extAttrCode[0].valueString : "-",
      "Source Timestamp": srcTmStmpExt[0]
        ? formattedDateWithDefault(srcTmStmpExt[0].valueDateTime)
        : "-",
      "Address Start Date":
        elementAddr && elementAddr.period && elementAddr.period.start
          ? formattedDateWithDefault(elementAddr.period.start)
          : "-",
      "Address End Date":
        elementAddr && elementAddr.period && elementAddr.period.end
          ? formattedDateWithDefault(elementAddr.period.end)
          : "-",
      "Mark as": getOverrideType(extensionMap.originalFieldName, address),
      originalFieldData: elementAddr,
      "Overridden By": overriddenByValue ? overriddenByValue : "-",
      "Override Reason": overrideReasonValue ? overrideReasonValue : "-",
      "Override Timestamp": overrideTimestampValue
        ? formattedDateWithDefault(overrideTimestampValue)
        : "-",
    };
  }
  return extensionMap;
};

export const overrideSummaryForIdentifier = (identifier, type) => {
  const extensionArray = identifier.hasOwnProperty("extension")
    ? identifier.extension.filter(
        (item) =>
          item.url ===
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
      )
    : [];

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(identifier);

  let extensionMap = {
    Field: "Identifiers",
    overrideIconClicked: true,
    originalFieldName: "identifier",
    reviewOverrideType: type,
  };

  if (extensionArray.length > 0) {
    const elementIdentity = extensionArray[0].valueIdentifier;
    extensionMap = {
      ...extensionMap,
      System: elementIdentity.system
        ? elementIdentity.system
        : elementIdentity.type &&
            elementIdentity.type.coding &&
            elementIdentity.type.coding[0] &&
            elementIdentity.type.coding[0].code
          ? elementIdentity.type.coding[0].code
          : "-",
      Value: elementIdentity.value ? elementIdentity.value : "-",
      Use: elementIdentity.use ? capitalize(elementIdentity.use) : "-",
      "Source Timestamp":
        elementIdentity.period && elementIdentity.period.start
          ? formattedDateWithDefault(elementIdentity.period.start)
          : "-",
      "Mark as": getOverrideType(extensionMap.originalFieldName, identifier),
      originalFieldData: elementIdentity,
      "Overridden By": overriddenByValue ? overriddenByValue : "-",
      "Override Reason": overrideReasonValue ? overrideReasonValue : "-",
      "Override Timestamp": overrideTimestampValue
        ? formattedDateWithDefault(overrideTimestampValue)
        : "-",
    };
  }
  return extensionMap;
};

export const overrideSummaryForCoverageIdentifierImdm = (identifier, type) => {
  const extensionArray = identifier.hasOwnProperty("extension")
    ? identifier.extension.filter(
        (item) =>
          item.url ===
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
      )
    : [];

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(identifier);

  let extensionMap = {
    Field: "Coverage",
    overrideIconClicked: true,
    originalFieldName: "identifier",
    reviewOverrideType: type,
  };

  if (extensionArray.length > 0) {
    const coverageElement = JSON.parse(extensionArray[0].valueIdentifier.value);
    extensionMap = {
      ...extensionMap,
      "Policy Number": coverageElement.hasOwnProperty("policyNum")
        ? coverageElement.policyNum
        : "-",
      "Dependent Code": coverageElement.hasOwnProperty("dependentCd")
        ? coverageElement.dependentCd
        : "-",
      "Division Code": coverageElement.hasOwnProperty("divisionCd")
        ? coverageElement.divisionCd
        : "-",
      "Relationship Code": coverageElement.hasOwnProperty("relationshipCd")
        ? coverageElement.relationshipCd
        : "-",
      "Family Id": coverageElement.hasOwnProperty("familyID")
        ? coverageElement.familyID
        : "-",
      "Member Id": coverageElement.hasOwnProperty("memberID")
        ? coverageElement.memberID
        : "-",
      "Subscriber Id": coverageElement.hasOwnProperty("subscriberID")
        ? coverageElement.subscriberID
        : "-",
      "Carrier Id": coverageElement.hasOwnProperty("carrierID")
        ? coverageElement.carrierID
        : "-",
      "Account Id": coverageElement.hasOwnProperty("accountID")
        ? coverageElement.accountID
        : "-",
      "Group Id": coverageElement.hasOwnProperty("groupID")
        ? coverageElement.groupID
        : "-",
      "Original System Id": coverageElement.hasOwnProperty("origSysId")
        ? coverageElement.origSysId
        : "-",
      "Medicaid RN": coverageElement.hasOwnProperty("medicaidRN")
        ? coverageElement.medicaidRN
        : "-",
      "Source Timestamp":
        extensionArray[0].valueIdentifier.period &&
        extensionArray[0].valueIdentifier.period.start
          ? formattedDateWithDefault(
              extensionArray[0].valueIdentifier.period.start,
            )
          : "-",
      "Mark as": getOverrideType(extensionMap.originalFieldName, identifier),
      originalFieldData: extensionArray[0].valueIdentifier,
      "Overridden By": overriddenByValue ? overriddenByValue : "-",
      "Override Reason": overrideReasonValue ? overrideReasonValue : "-",
      "Override Timestamp": overrideTimestampValue
        ? formattedDateWithDefault(overrideTimestampValue)
        : "-",
    };
  }
  return extensionMap;
};

export const overrideSummaryForGender = (data, overrideExtension, type) => {
  let extensionMap = {
    Field: "Gender",
    overrideIconClicked: true,
    originalFieldName: "gender",
    reviewOverrideType: type,
  };

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(overrideExtension);

  if (overrideExtension && overrideExtension.length > 0) {
    extensionMap["Gender"] = capitalize(overrideExtension[0].valueString);
    extensionMap["Mark as"] = data && data !== null ? "Replace" : "Invalid";
    extensionMap["Overridden By"] = overriddenByValue ? overriddenByValue : "-";
    extensionMap["Override Reason"] = overrideReasonValue
      ? overrideReasonValue
      : "-";
    extensionMap["Override Timestamp"] = overrideTimestampValue
      ? formattedDateWithDefault(overrideTimestampValue)
      : "-";
  }
  return extensionMap;
};

export const overrideSummaryForBirthDate = (data, overrideExtension, type) => {
  let extensionMap = {
    Field: "Date of Birth",
    overrideIconClicked: true,
    originalFieldName: "birthDate",
    reviewOverrideType: type,
  };
  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(overrideExtension);

  if (overrideExtension && overrideExtension.length > 0) {
    extensionMap["Date Of Birth"] = overrideExtension[0].valueDate
      ? moment(overrideExtension[0].valueDate).format("L")
      : "None";
    extensionMap["Mark as"] = data && data !== null ? "Replace" : "Invalid";
    extensionMap["Overridden By"] = overriddenByValue ? overriddenByValue : "-";
    extensionMap["Override Reason"] = overrideReasonValue
      ? overrideReasonValue
      : "-";
    extensionMap["Override Timestamp"] = overrideTimestampValue
      ? formattedDateWithDefault(overrideTimestampValue)
      : "-";
  }
  return extensionMap;
};
export const overrideSummaryForMultipleBirth = (
  data,
  overrideExtension,
  type,
) => {
  let extensionMap = {
    Field: "Multiple Birth",
    overrideIconClicked: true,
    originalFieldName: "multipleBirthBoolean",
    reviewOverrideType: type,
  };

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(overrideExtension);

  if (overrideExtension && overrideExtension.length > 0) {
    extensionMap["Multiple Birth"] =
      overrideExtension[0].valueString === "new"
        ? "-"
        : overrideExtension[0].valueString
          ? "Yes"
          : "No";
    extensionMap["Mark as"] =
      overrideExtension[0].valueString === "new"
        ? "New"
        : data && data !== null
          ? "Replace"
          : "Invalid";
    extensionMap["Overridden By"] = overriddenByValue ? overriddenByValue : "-";
    extensionMap["Override Reason"] = overrideReasonValue
      ? overrideReasonValue
      : "-";
    extensionMap["Override Timestamp"] = overrideTimestampValue
      ? formattedDateWithDefault(overrideTimestampValue)
      : "-";
  }
  return extensionMap;
};

export const overrideSummaryForTelecom = (telecom, type) => {
  const extensionArray = telecom.hasOwnProperty("extension")
    ? telecom.extension.filter(
        (item) =>
          item.url ===
          "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#override",
      )
    : [];

  const { overriddenByValue, overrideReasonValue, overrideTimestampValue } =
    extractAuditExtensionValues(telecom);

  let extensionMap = {
    Field: "Contact Information",
    overrideIconClicked: true,
    originalFieldName: "telecom",
    reviewOverrideType: type,
  };

  if (extensionArray.length > 0) {
    const elementContact = extensionArray[0].valueContactPoint;
    const extAttrCode =
      elementContact && elementContact.extension
        ? elementContact.extension.filter((e) => e.url.includes("attrCode"))
        : "";
    extensionMap = {
      ...extensionMap,
      System:
        elementContact && elementContact.system
          ? capitalize(elementContact.system)
          : "-",
      Value:
        elementContact && elementContact.value ? elementContact.value : "-",
      Use:
        elementContact && elementContact.use
          ? capitalize(elementContact.use)
          : "-",
      Attribute:
        elementContact && extAttrCode[0] ? extAttrCode[0].valueString : "",
      "Source Timestamp":
        elementContact && elementContact.period && elementContact.period.start
          ? formattedDateWithDefault(elementContact.period.start)
          : "-",
      "Mark as": getOverrideType(extensionMap.originalFieldName, telecom),
      originalFieldData: elementContact,
      "Overridden By": overriddenByValue ? overriddenByValue : "-",
      "Override Reason": overrideReasonValue ? overrideReasonValue : "-",
      "Override Timestamp": overrideTimestampValue
        ? formattedDateWithDefault(overrideTimestampValue)
        : "-",
    };
  }
  return extensionMap;
};

export const listOfAddressTypes = () => {
  return [
    {
      use: "home",
      attrCode: "PERPRIMERRES",
      displayName: "Per Primery Residence",
    },
    {
      use: "work",
      attrCode: "PERBUSADDR",
      displayName: "Per Business Address",
    },
    {
      use: "",
      attrCode: "PERMAILADDR",
      displayName: "Per Mailing address",
    },
    {
      use: "temp",
      attrCode: "PERTEMPADDR",
      displayName: "Per Temporary Address",
    },
    {
      use: "",
      attrCode: "PERSECONDRES",
      displayName: "Per Secoundary Residence",
    },
    {
      use: "",
      attrCode: "PERUNKADDR",
      displayName: "Per Unique Address",
    },
    {
      use: "",
      attrCode: "PERRXSHIPADR",
      displayName: "Per RXSHIP Address",
    },
    {
      use: "",
      attrCode: "PERTSHIPADDR",
      displayName: "Per TSHIP Address",
    },
    {
      use: "",
      attrCode: "PERSHIPADDR",
      displayName: "Per Shiping Address",
    },
    {
      use: "billing",
      attrCode: "PERBILLADR",
      displayName: "Per Billing Address",
    },
    {
      use: "",
      attrCode: "PERTMAILADDR",
      displayName: "Per TMAIL Address",
    },
    {
      use: "",
      attrCode: "PERMRMAILING",
      displayName: "Per MRMAILING",
    },
    {
      use: "",
      attrCode: "PERNCOAADDR",
      displayName: "Per NCOA Address",
    },
  ];
};
export const listOfPhoneTypes = () => {
  return [
    {
      use: "home",
      attrCode: "PERHOMEPHONE",
      displayName: "Per Home Phone",
    },
    {
      use: "work",
      attrCode: "PERBUSPHONE",
      displayName: "Per Business Phone",
    },
    {
      use: "mobile",
      attrCode: "PERMOBILE",
      displayName: "Per Mobile",
    },
    {
      use: "",
      attrCode: "PERUNKPHONE",
      displayName: "Per Unknown Phone",
    },
    {
      use: "temp",
      attrCode: "PERADDLPHONE",
      displayName: "Per Additional Phone",
    },
    {
      use: "",
      attrCode: "PERREGPHONE",
      displayName: "Per Reg Phone",
    },
  ];
};

export const listOfEmailTypes = () => {
  return [
    {
      use: "work",
      attrCode: "PERBUSEMAIL",
      displayName: "Per Business Email",
    },
    {
      use: "",
      attrCode: "PERPERSEMAIL",
      displayName: "Per Personal Email",
    },
    {
      use: "home",
      attrCode: "PERPRIEMAIL",
      displayName: "Per Primary Email",
    },
    {
      use: "",
      attrCode: "PERUNKEMAIL",
      displayName: "Per Unknown Email",
    },
    {
      use: "temp",
      attrCode: "PERSECEMAIL",
      displayName: "Per Secoundry Email",
    },
    {
      use: "",
      attrCode: "PERREGEMAIL",
      displayName: "Per Reg Email",
    },
  ];
};

function extractAuditExtensionValues(objOrArray) {
  // If input is an array, use it directly; otherwise, use obj.extension
  const extensions = Array.isArray(objOrArray)
    ? objOrArray
    : objOrArray && objOrArray.extension
      ? objOrArray.extension
      : [];

  const overriddenByValue = (
    extensions.find((item) =>
      [
        ExtensionURLs.overriddenBy,
        ExtensionURLs.dobOverriddenBy,
        ExtensionURLs.genderOverriddenBy,
        ExtensionURLs.multipleBirthOverriddenBy,
      ].includes(item.url),
    ) || {}
  ).valueString;

  const overrideReasonValue = (
    extensions.find((item) =>
      [
        ExtensionURLs.overrideReason,
        ExtensionURLs.dobOverrideReason,
        ExtensionURLs.genderOverrideReason,
        ExtensionURLs.multipleBirthOverrideReason,
      ].includes(item.url),
    ) || {}
  ).valueString;

  const overrideTimestampExtension =
    extensions.find((item) =>
      [
        ExtensionURLs.overrideTimestamp,
        ExtensionURLs.dobOverrideTimestamp,
        ExtensionURLs.genderOverrideTimestamp,
        ExtensionURLs.multipleBirthOverrideTimestamp,
      ].includes(item.url),
    ) || {};

  const overrideTimestampValue =
    overrideTimestampExtension?.valueDateTime ||
    overrideTimestampExtension?.valueString;

  return { overriddenByValue, overrideReasonValue, overrideTimestampValue };
}

/**
 * Create restore extensions for the given multi valued attribute and value. This includes the restore audit
 * extensions as well as the restore extension itself.
 *
 * @param {string} attribute - The attribute being processed.
 * @param {Object} itemJSON - The restore item to be used as the restore extension value.
 * @returns {Array} - An array of restore extension objects.
 */
export const createRestoreExtensions = (attribute, auditDetails) => {
  // Create restore audit extensions (restore-updatedBy, restore-reason, restore-timestamp)
  const restoreAuditExtensions = createRestoreAuditExtensions(
    auditDetails,
    attribute,
    ActionLabels.RESTORE,
  );

  return [...restoreAuditExtensions];
};
